import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import { COLORS } from '../../styles/colors';

export type ButtonType =
  | 'alert'
  | 'classic'
  | 'disabled'
  | 'validation'
  | 'bordered'
  | 'neutral';

export interface WxButtonProps {
  label: string;
  onClick: (arg: any) => void;
  type: ButtonType;
  darkMode?: boolean;
  expand?: boolean;
  disabled?: boolean;
  noArrow?: boolean;
}

/** WxButton Documentation
 * expand prop let button expand to max-width of its container (default: false)
 */
const WxButton = ({
  onClick,
  label,
  type,
  darkMode = false,
  expand = false,
  disabled = false,
  noArrow = false,
}: WxButtonProps) => {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      data-type={type}
      data-disabled={disabled}
      $darkMode={darkMode}
      $expand={expand}
    >
      {label}
      {!noArrow && <FontAwesomeIcon icon={faArrowRight} />}
    </Button>
  );
};

const Button = styled.button<{
  $darkMode: boolean;
  $expand: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;

  background-color: var(--bg-color, ${COLORS.transparent});
  border: 1px solid var(--border-color, ${COLORS.transparent});
  color: var(--color, ${COLORS.white});
  cursor: pointer;

  text-transform: uppercase;
  border-radius: 0;
  font-size: 0.75rem;
  line-height: 1.16;
  min-height: 2.125rem;
  font-family: 'Roboto';
  font-weight: 900;
  width: ${({ $expand }) => ($expand ? '100%' : 'fit-content')};
  flex: ${({ $expand }) => ($expand ? '1' : 'unset')};
  gap: 0.625em;
  padding: 0.625em;

  &[data-type='validation'] {
    --bg-color: ${COLORS.green};
    &[data-disabled='true'] {
      opacity: 0.2;
      pointer-events: none;
    }
  }

  &[data-type='neutral'] {
    --color: ${COLORS.white};
    --bg-color: #44444c;
    &[data-disabled='true'] {
      opacity: 0.2;
      pointer-events: none;
    }
  }

  &[data-type='alert'] {
    --color: ${COLORS.white};
    --bg-color: ${COLORS.red};
    &[data-disabled='true'] {
      opacity: 0.2;
      pointer-events: none;
    }
  }

  &[data-type='classic'] {
    --bg-color: ${(props) => (props.$darkMode ? COLORS.white : COLORS.black)};
    --color: ${(props) => (props.$darkMode ? COLORS.black : COLORS.white)};
    &[data-disabled='true'] {
      opacity: 0.2;
      pointer-events: none;
    }
  }

  &[data-type='bordered'] {
    --color: ${(props) => (props.$darkMode ? COLORS.white : COLORS.black)};
    --border-color: ${(props) =>
      props.$darkMode ? COLORS.white : COLORS.transparent};
    &[data-disabled='true'] {
      opacity: 0.2;
      pointer-events: none;
    }
  }

  &:hover {
    &[data-type='validation'] {
      --color: ${COLORS.white};
      --bg-color: hsl(151, 100%, 40%);
    }
    &[data-type='neutral'] {
      --color: ${COLORS.white};
      --bg-color: #66666c;
    }
    &[data-type='alert'] {
      --color: ${COLORS.white};
      --bg-color: red;
    }
    &[data-type='classic'] {
      --bg-color: ${(props) =>
        props.$darkMode ? COLORS.lightgray : COLORS.darkgray};
    }
    &[data-type='bordered'] {
      --color: ${(props) => (props.$darkMode ? COLORS.black : COLORS.white)};
      --border-color: ${(props) =>
        props.$darkMode ? COLORS.white : COLORS.transparent};
      --bg-color: ${(props) => (props.$darkMode ? COLORS.white : COLORS.black)};
    }
  }
  > * {
    white-space: nowrap;
  }
`;

export default WxButton;
